import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useBusinessId } from '@/hooks/useBusinessId';
import { appointmentsApi } from '@/api/appointments';
import { teamApi, type TimeSlot } from '@/api/team';
import { formatTime } from '@/utils/time';
import { addDays, formatDateLabel, todayStr } from '@/utils/date';
import { ArrowLeft, Loader2, Calendar, Clock, Check } from 'lucide-react';

export function AppointmentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const businessId = useBusinessId();
  const queryClient = useQueryClient();
  const appointmentId = Number(id);

  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  const { data: appointment, isLoading } = useQuery({
    queryKey: ['appointment', businessId, appointmentId],
    queryFn: () => appointmentsApi.getById(businessId, appointmentId),
    enabled: !!businessId && !isNaN(appointmentId),
  });

  // Sync local selection from the appointment whenever it (re)loads.
  const serverDate = appointment?.appointmentDate;
  const serverStartTime = appointment?.startTime;
  useEffect(() => {
    if (serverDate == null) return;
    setSelectedDate(serverDate);
    setSelectedTime(serverStartTime?.slice(0, 5) ?? '');
  }, [serverDate, serverStartTime]);

  const totalDuration = useMemo(() => {
    if (!appointment?.services?.length) return 30;
    return appointment.services.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0) || 30;
  }, [appointment]);

  const dateOptions = useMemo(() => {
    const today = todayStr();
    const window = Array.from({ length: 14 }, (_, i) => addDays(today, i));
    // Make sure the appointment's current date is always selectable, even if it
    // falls outside the today..+13 window (past or far-future appointments).
    const current = appointment?.appointmentDate;
    if (current && !window.includes(current)) {
      return [current, ...window].sort();
    }
    return window;
  }, [appointment?.appointmentDate]);

  const { data: timeSlots = [], isLoading: slotsLoading } = useQuery({
    queryKey: ['availability', businessId, appointment?.userId, selectedDate, totalDuration],
    queryFn: () => teamApi.getAvailability(businessId, appointment!.userId, selectedDate, totalDuration),
    enabled: !!businessId && !!appointment?.userId && !!selectedDate,
    staleTime: 30_000,
  });

  const availableSlots = timeSlots.filter((s: TimeSlot) => s.available);

  const hasChanges = appointment && (
    selectedDate !== appointment.appointmentDate ||
    selectedTime !== (appointment.startTime?.slice(0, 5) ?? '')
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!appointment) throw new Error('No appointment');
      // A line item is either a service (serviceId) or a package (servicePackageId).
      // The API takes them as two separate lists, so split rather than forcing
      // everything into services[] (which would emit serviceId: undefined for packages).
      const items = appointment.services ?? [];
      const services = items
        .filter(s => s.serviceId != null)
        .map(s => ({ serviceId: s.serviceId! }));
      const packageIds = items
        .filter(s => s.servicePackageId != null)
        .map(s => s.servicePackageId!);
      return appointmentsApi.update(businessId, appointmentId, {
        customerId: appointment.customerId,
        userId: appointment.userId,
        appointmentDate: selectedDate,
        startTime: selectedTime,
        stateId: appointment.stateId,
        services,
        packageIds,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointment', businessId, appointmentId] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      toast.success('Cita reagendada');
      navigate(-1);
    },
    onError: () => toast.error('No se pudo reagendar la cita'),
  });

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--cream)' }}>
        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--terracotta)' }} />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4" style={{ backgroundColor: 'var(--cream)' }}>
        <div style={{ fontSize: '14px', color: 'var(--muted-fg)' }}>Cita no encontrada</div>
        <button onClick={() => navigate(-1)} style={{ fontSize: '14px', color: 'var(--terracotta)', cursor: 'pointer', background: 'none', border: 'none' }}>
          <ArrowLeft size={16} className="inline mr-1" />Volver
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      <div className="flex-1 overflow-auto" style={{ paddingTop: '20px', paddingLeft: '14px', paddingRight: '14px', paddingBottom: '96px' }}>

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 rounded-lg transition-colors"
            style={{ padding: '6px 10px', fontSize: '14px', color: 'var(--terracotta)', cursor: 'pointer', marginLeft: '-10px', background: 'none', border: 'none' }}
          >
            <ArrowLeft size={18} strokeWidth={2} />
            <span>Volver</span>
          </button>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}>
            Reagendar cita
          </div>
        </div>

        {/* Current appointment summary */}
        <div className="rounded-xl mb-5" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '14px 18px' }}>
          <div className="flex items-center gap-3">
            <div className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: '36px', height: '36px', backgroundColor: 'var(--peach)', color: 'var(--dark-teal)', fontFamily: 'var(--font-heading)', fontSize: '12px', fontWeight: 600 }}>
              {appointment.customerName?.split(' ').map(n => n[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate" style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark-teal)' }}>{appointment.customerName}</div>
              <div className="truncate" style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>{appointment.serviceName} · {totalDuration} min · {appointment.userName}</div>
            </div>
          </div>
        </div>

        {/* Date picker */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={16} style={{ color: 'var(--terracotta)' }} />
            <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark-teal)' }}>Nueva fecha</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {dateOptions.map(date => {
              const isSelected = date === selectedDate;
              return (
                <button
                  key={date}
                  onClick={() => { setSelectedDate(date); setSelectedTime(''); }}
                  className="flex-shrink-0 rounded-xl text-center transition-colors"
                  style={{
                    minWidth: '64px', padding: '10px 12px',
                    backgroundColor: isSelected ? 'var(--terracotta)' : 'var(--off-white)',
                    color: isSelected ? 'white' : 'var(--dark-teal)',
                    border: isSelected ? 'none' : '1px solid var(--border-color)',
                    cursor: 'pointer', fontSize: '13px', fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {formatDateLabel(date)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time picker */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} style={{ color: 'var(--terracotta)' }} />
            <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark-teal)' }}>Nueva hora</span>
          </div>
          {slotsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin" style={{ color: 'var(--muted-fg)' }} />
            </div>
          ) : availableSlots.length === 0 ? (
            <div className="rounded-xl text-center py-6" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', fontSize: '13px', color: 'var(--muted-fg)' }}>
              Sin horarios disponibles para esta fecha
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {availableSlots.map((slot: TimeSlot) => {
                const time = slot.time.slice(0, 5);
                const isSelected = time === selectedTime;
                return (
                  <button
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    className="rounded-lg text-center transition-colors"
                    style={{
                      padding: '10px 8px',
                      backgroundColor: isSelected ? 'var(--terracotta)' : 'var(--off-white)',
                      color: isSelected ? 'white' : 'var(--dark-teal)',
                      border: isSelected ? 'none' : '1px solid var(--border-color)',
                      cursor: 'pointer', fontSize: '13px', fontWeight: isSelected ? 600 : 400,
                    }}
                  >
                    {formatTime(time)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg transition-colors"
            style={{ height: '44px', backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', color: 'var(--dark-teal)', fontSize: '14px', cursor: 'pointer' }}
          >
            Cancelar
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || !selectedTime || saveMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg transition-colors disabled:opacity-50"
            style={{ height: '44px', backgroundColor: 'var(--terracotta)', color: 'white', fontSize: '14px', fontWeight: 500, border: 'none', cursor: hasChanges && selectedTime ? 'pointer' : 'not-allowed' }}
          >
            {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Reagendar
          </button>
        </div>
      </div>
    </div>
  );
}
